/**
 * @fileoverview Section "Icons" — set Lucide utilisé dans GNPC.
 *
 * Ne montre pas les 1000+ icônes Lucide, mais uniquement celles qui ont
 * un usage identifié dans les flux GNPC (recherche, paiement, MFA, agent,
 * intégrations, etc.). Ajouter une icône ici uniquement quand elle est
 * réellement utilisée dans un composant livré.
 */

import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  FileText,
  Filter,
  HelpCircle,
  Info,
  KeyRound,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  User,
  X,
  type LucideIcon,
} from 'lucide-react';
import { CopyButton } from '@gnpc/ui';

interface IconGroupProps {
  title: string;
  icons: ReadonlyArray<{ name: string; Icon: LucideIcon; usage: string }>;
}

function IconGroup({ title, icons }: IconGroupProps): JSX.Element {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-quipux-navy-950">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {icons.map(({ name, Icon, usage }) => (
          <div
            key={name}
            className="group flex items-start gap-3 rounded-md border border-border p-3"
          >
            <Icon className="size-5 shrink-0 text-quipux-navy-800" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <code className="truncate font-mono text-xs text-quipux-navy-900">{name}</code>
                <CopyButton value={name} ariaLabel={`Copier l'import ${name}`} />
              </div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">{usage}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DsSectionIcons(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="rounded-lg border border-quipux-blue-200 bg-quipux-blue-50 p-4 text-xs text-quipux-navy-900">
        Ensemble <a
          href="https://lucide.dev/icons"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-quipux-blue-700 underline underline-offset-2"
        >Lucide</a>. Import :{' '}
        <code className="rounded bg-white/70 px-1.5 py-0.5">
          import {'{'} Search {'}'} from 'lucide-react'
        </code>
        . Tailles usuelles <code>size-3.5</code> · <code>size-4</code> (défaut) ·{' '}
        <code>size-5</code>.
      </div>

      <IconGroup
        title="Actions"
        icons={[
          { name: 'Search', Icon: Search, usage: 'Champ de recherche' },
          { name: 'Plus', Icon: Plus, usage: 'Ajouter, inviter' },
          { name: 'X', Icon: X, usage: 'Fermer, retirer' },
          { name: 'Check', Icon: Check, usage: 'Validation' },
          { name: 'Copy', Icon: Copy, usage: 'Copier valeur' },
          { name: 'Download', Icon: Download, usage: 'Télécharger PDF' },
          { name: 'RefreshCw', Icon: RefreshCw, usage: 'Rejouer, resynchroniser' },
          { name: 'Filter', Icon: Filter, usage: 'Filtres tableau' },
        ]}
      />

      <IconGroup
        title="Navigation"
        icons={[
          { name: 'ArrowLeft', Icon: ArrowLeft, usage: 'Retour' },
          { name: 'ArrowRight', Icon: ArrowRight, usage: 'Suivant' },
          { name: 'ChevronDown', Icon: ChevronDown, usage: 'Dropdown, accordion' },
          { name: 'ChevronRight', Icon: ChevronRight, usage: 'Item cliquable' },
          { name: 'Menu', Icon: Menu, usage: 'Menu mobile' },
          { name: 'LogOut', Icon: LogOut, usage: 'Déconnexion' },
        ]}
      />

      <IconGroup
        title="Statuts & feedback"
        icons={[
          { name: 'AlertCircle', Icon: AlertCircle, usage: 'Erreur formulaire' },
          { name: 'AlertTriangle', Icon: AlertTriangle, usage: 'Alerte paiement, dégradé' },
          { name: 'CheckCircle2', Icon: CheckCircle2, usage: 'Succès, PAID' },
          { name: 'Info', Icon: Info, usage: 'Note, message informationnel' },
          { name: 'HelpCircle', Icon: HelpCircle, usage: 'FormField tooltip' },
          { name: 'Clock', Icon: Clock, usage: 'PENDING, en cours' },
          { name: 'Loader2', Icon: Loader2, usage: 'Spinner (animate-spin)' },
          { name: 'TrendingUp', Icon: TrendingUp, usage: 'Delta positif dashboard' },
        ]}
      />

      <IconGroup
        title="Métier GNPC"
        icons={[
          { name: 'CreditCard', Icon: CreditCard, usage: 'Paiement, transaction' },
          { name: 'FileText', Icon: FileText, usage: 'Quittance, justificatif' },
          { name: 'Smartphone', Icon: Smartphone, usage: 'Mobile money, OTP SMS' },
          { name: 'ShieldCheck', Icon: ShieldCheck, usage: 'MFA, sécurité' },
          { name: 'KeyRound', Icon: KeyRound, usage: 'Mot de passe' },
          { name: 'Mail', Icon: Mail, usage: 'Email agent' },
          { name: 'Phone', Icon: Phone, usage: 'Téléphone citoyen' },
          { name: 'User', Icon: User, usage: 'Agent, profil' },
          { name: 'Lock', Icon: Lock, usage: 'Champ verrouillé, session' },
          { name: 'Eye', Icon: Eye, usage: 'Afficher mot de passe' },
          { name: 'EyeOff', Icon: EyeOff, usage: 'Masquer mot de passe' },
          { name: 'MessageSquare', Icon: MessageSquare, usage: 'Notifications SMS' },
          { name: 'Settings', Icon: Settings, usage: 'Paramètres compte' },
        ]}
      />
    </div>
  );
}
